import { Fragment, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { ColumnPicker } from "@/components/ColumnPicker";
import {
  EquipmentPagination,
  EquipmentTable,
} from "@/components/equipment/EquipmentTable";
import { useEquipment } from "@/hooks/useEquipment";
import { useEquipmentTags, type EquipmentTag } from "@/hooks/useEquipmentTags";
import { useEquipmentListColumns } from "@/hooks/useEquipmentListColumns";
import { useEquipmentListParams } from "@/hooks/useEquipmentListParams";

const EquipmentTagPage = () => {
  const { tagId } = useParams<{ tagId: string }>();
  const { data: tags = [], isLoading: tagsLoading } = useEquipmentTags();
  const { search, page, setSearch, setPage } = useEquipmentListParams();

  const tag = tags.find((t) => t.id === tagId);

  const ancestors = useMemo(() => {
    const byId = new Map(tags.map((t) => [t.id, t]));
    const result: EquipmentTag[] = [];
    let parentId = tag?.parent_id ?? null;
    while (parentId && !result.some((t) => t.id === parentId)) {
      const parent = byId.get(parentId);
      if (!parent) break;
      result.unshift(parent);
      parentId = parent.parent_id;
    }
    return result;
  }, [tags, tag]);

  const childTags = useMemo(
    () => tags.filter((t) => t.parent_id === tagId),
    [tags, tagId],
  );

  const tagIds = useMemo(() => (tagId ? [tagId] : []), [tagId]);
  const { data, isLoading, isFetching } = useEquipment({
    search,
    tagIds,
    page,
  });

  const { columns, visibleColumns, setColumnVisible, resetColumns } =
    useEquipmentListColumns();

  if (!tag) {
    return (
      <div className="space-y-6">
        <PageHeader title="Equipment" />
        <p className="text-muted-foreground">
          {tagsLoading ? "Loading..." : "This tag does not exist."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={tag.name} />

      <div className="space-y-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/equipment">Equipment</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {ancestors.map((ancestor) => (
              <Fragment key={ancestor.id}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/equipment/tag/${ancestor.id}`}>
                      {ancestor.name}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Fragment>
            ))}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage data-testid="equipment-tag-page-title">
                {tag.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {childTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {childTags.map((child) => (
              <Link key={child.id} to={`/equipment/tag/${child.id}`}>
                <Badge variant="outline" className="hover:bg-muted">
                  {child.name}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={`Search in ${tag.name}...`}
          />
        </div>
        <ColumnPicker
          columns={columns}
          visibleColumns={visibleColumns}
          onColumnVisibleChange={setColumnVisible}
          onReset={resetColumns}
          testIdPrefix="equipment"
        />
      </div>

      <EquipmentTable
        items={data?.items ?? []}
        columns={visibleColumns}
        isLoading={isLoading}
        isFetching={isFetching}
      />
      <EquipmentPagination
        page={page}
        totalPages={data?.totalPages ?? 1}
        totalCount={data?.totalCount ?? 0}
        onPageChange={setPage}
      />
    </div>
  );
};

export default EquipmentTagPage;
